import { useFormik } from 'formik';
import { useState } from 'react';
import { FiChevronLeft } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';

import {
  bgMinileague,
  icCamera,
  icUserCheck,
  icUserPlus,
} from '@/assets/tournaments';
import { Button, Form, InputField, Modal } from '@/components';
import { Layout, Sidebar } from '@/components/GenericsTournaments';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useMinileagueData } from '@/hooks/useMinileagueData';
import useToast from '@/hooks/useToast';
import ROUTES from '@/router/routes';
import {
  createMiniLeague,
  requestCheckNameAvailability,
} from '@/services/minileagues';
import {
  cn,
  createMLSchema,
  oneSpaceBetweenWordMinTwoChar,
  TEXTS,
  validateFieldError,
} from '@/utils';
import { SECTION_NAME_FOR_ADS } from '@/utils/ads';

type TypeProps = {
  showModal: boolean;
};

const initialState = {
  showModal: false,
};

const minileagueTips = [
  {
    icon: <img className="inline" src={icUserPlus} />,
    text: 'Invitá a quien quieras.',
  },
  {
    icon: <img className="inline" src={icUserCheck} />,
    text: 'Aceptá a quien se postule en tu Miniliga.',
  },
  {
    icon: <img className="inline" src={icCamera} />,
    text: 'Subí una foto que identifique tu Miniliga.',
  },
];

export const CreateML = () => {
  const toast = useToast();
  const { isDesktop } = useIsDesktop();
  const navigate = useNavigate();
  const [state, setState] = useState<TypeProps>(initialState);
  const { updateMinileagueProperties } = useMinileagueData();

  const {
    touched,
    errors,
    isSubmitting,
    isValid,
    setFieldValue,
    handleSubmit,
    values,
    handleBlur,
    setFieldError,
    setStatus,
    setFieldTouched,
    status,
  } = useFormik({
    validateOnMount: false,
    validateOnBlur: true,
    validationSchema: createMLSchema,
    initialValues: {
      groupName: '',
    },
    onSubmit: async (
      {
        groupName,
      }: {
        groupName: string;
      },
      { setSubmitting }
    ) => {
      if (groupName) {
        try {
          const response = await createMiniLeague(groupName);

          if (response.estado) {
            setState({
              showModal: true,
            });
            updateMinileagueProperties({
              ...response,
            });
          } else {
            toast.show(response.mensaje);
          }

          setSubmitting(false);
        } catch (error) {
          console.error(error);
          throw new Error(TEXTS.defaultError);
        }
      }
    },
  });

  const MIN_CHARACTERS_TO_VALIDATE_NAME = 2;

  const handleNameTournamentValidation = async (groupName: string) => {
    if (groupName.trim().length >= MIN_CHARACTERS_TO_VALIDATE_NAME) {
      const response = await requestCheckNameAvailability(
        encodeURIComponent(groupName)
      );
      if (!response.estado && 'codError' in response) {
        if (response.codError === 1603) {
          setFieldError('groupName', TEXTS.nameExist);
          setStatus(false);
        } else {
          setFieldError('groupName', response.mensaje || TEXTS.defaultError);
          setStatus(false);
        }
      } else {
        setFieldError('groupName', undefined);
        setStatus(true);
      }
    } else {
      setStatus(false);
    }
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = oneSpaceBetweenWordMinTwoChar(e.target.value);

    setFieldValue('groupName', value);
    setFieldTouched('groupName', true, false);

    if (value.trim().length === 0) {
      setFieldError('groupName', TEXTS.minLetters);
      setStatus(false);
    } else {
      handleNameTournamentValidation(value);
    }
  };
  return (
    <Layout
      actionFooter={{
        actionHandler: handleSubmit,
        buttonName: 'Crear Miniliga',
        buttonDisabled:
          isSubmitting || !isValid || values.groupName?.length === 0,
      }}
      adsSections={SECTION_NAME_FOR_ADS.miniligas}
      className="px-4 lg:w-[974px] lg:mt-6"
      main={() => (
        <div className="lg:w-[400px]">
          {isDesktop && (
            <>
              <Link
                className="text-gdt-blue font-bold relative left-[-15px]"
                to={ROUTES.minileagues.home}
              >
                <FiChevronLeft className="text-gdt-blue w-6 h-6 inline" />
                <span className="pl-[4px]">Volver</span>
              </Link>
              <div className="mt-[40px]">
                <span className="font-bold text-base text-gdt-gray lg:text-lg">
                  Crear nueva Miniliga
                </span>
              </div>
            </>
          )}
          <p className="font-medium lg:font-semibold text-sm mt-4 text-gdt-gray-2">
            Creá tu Miniliga y competí en desafíos mano a mano todas las fechas
            para ganar tu pase a la final.
          </p>
          <div className="lg:w-[80%] mt-5 lg:mt-8">
            <Form.Field>
              <InputField
                behavior="default"
                error={validateFieldError(
                  touched.groupName,
                  errors.groupName,
                  values.groupName.length
                )}
                label="Nombre de Miniliga"
                maxLength={25}
                name="groupName"
                placeholder="Ingresar nombre"
                success={
                  !errors.groupName && values.groupName?.length > 0 && status
                }
                value={values.groupName}
                onBlur={(e: React.FocusEvent<any, Element>) => {
                  if (values.groupName.length < 2) {
                    handleBlur(e);
                  }
                }}
                onChange={handleInputChange}
              />
            </Form.Field>
          </div>
          {isDesktop && (
            <div className="w-52 mt-10">
              <Button
                className="w-full"
                disabled={
                  isSubmitting || !isValid || values.groupName?.length === 0
                }
                type="button"
                variant="primary"
                onClick={handleSubmit}
              >
                Crear nueva Miniliga
              </Button>
            </div>
          )}

          <Modal
            bottomSheetClassName="px-[16px] pt-[20px] pb-[40px]"
            className="px-4 lg:px-11 py-10 min-w-[550px]"
            closeOutside={false}
            controlledOnClose={() => null}
            isControlledOpen={state.showModal}
            render={() => {
              return (
                <div>
                  <p className="text-base font-semibold mb-[30px] text-center">
                    ¡Felicitaciones! La Miniliga fue creada con éxito.
                    <br />
                    ¿Querés invitar a tus amigos ahora?
                  </p>
                  <div className="flex space-x-[10px] h-[50px]">
                    <Button
                      className="flex-1 px-0"
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setState(initialState);
                        navigate(ROUTES.minileagues.home);
                      }}
                    >
                      Invitar más tarde
                    </Button>
                    <Button
                      className="flex-1 px-0"
                      type="button"
                      variant="primary"
                      onClick={() => {
                        setState(initialState);
                        navigate(ROUTES.minileagues.invite);
                      }}
                    >
                      Aceptar
                    </Button>
                  </div>
                </div>
              );
            }}
            showCloseButton={false}
            size="xs"
            variant="controlled"
          />
        </div>
      )}
      mobile={{
        leftContent: {
          backAction: () => {
            navigate(ROUTES.minileagues.home);
          },
        },
        centerContent: { title: 'Crear Miniliga' },
      }}
      sidebar={() =>
        isDesktop && (
          <Sidebar className="lg:w-[436px]">
            <div
              className={cn(
                'relative overflow-hidden rounded-md bg-no-repeat bg-left bg-cover', // h-[290px]
                'h-[369px] w-[426px] bg-[length:426px_369px] mb-3'
              )}
              style={{
                backgroundImage: `url(${bgMinileague})`,
              }}
            >
              <div className="pl-[38px] pt-[161px] relative">
                <p className="text-[31px]/[30px] font-normal font-bebas text-white w-[240px]">
                  COMO ADMINISTRADOR DE UNA MINILIGA:
                </p>
                <ul>
                  {minileagueTips.map((tip, index) => (
                    <li
                      key={index}
                      className="my-[8px] text-[15px] flex items-center"
                    >
                      {tip.icon}
                      <span className="text-white ml-[5px]">{tip.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Sidebar>
        )
      }
      withBanners={false}
    />
  );
};
